import React from 'react'
import { CloseOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Alert, Button, Select, Spin, Tooltip, Typography } from 'antd'
import debounce from 'lodash.debounce'
import { appApi } from 'api/app'
import { cdekApi } from 'api/cdek'
import { ordersApi } from 'api/orders'
import { yandexDeliveryApi } from 'api/yandexDelivery'
import { IAddressSuggestion } from 'models/app'
import { ERoles } from 'models/auth'
import {
  IShipmentPointQuoteResponse,
  ITableOrder,
  TPickupPointProvider
} from 'models/orders'
import { DEBOUNCE_DELAY } from 'shared/constants/app'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import {
  BOUNDS_DELAY,
  CDEK_LOADED_RADIUS_KM,
  CITY_ZOOM,
  CURRENT_PRESET,
  DEFAULT_CENTER,
  ICityOption,
  IMapPoint,
  MAP_LOOK,
  PICKED_PRESET,
  TCoords,
  distanceKm,
  fromCdekOffice,
  fromYandexStation,
  getCdekCell
} from 'shared/utils/shipmentPoints'
import { loadYandexMaps } from 'shared/utils/yandexMaps'
import { appSelectors } from 'store/app'
import { getShipmentProvider } from './ChangeShipmentPoint'
import { ShipmentPointChangeModal } from './ShipmentPointChangeModal'
import styles from './ChangeShipmentPoint.module.scss'

interface IShipmentPointPickerProps {
  order: ITableOrder
  currentAddress?: string
  onClose: () => void
  onChanged: () => Promise<unknown>
}

interface IMapStart {
  center: TCoords
  zoom: number
  currentPoint: IMapPoint | null
}

const POINT_ZOOM = 13

const HELP: Record<TPickupPointProvider, string> = {
  CDEK: 'Если накладная СДЭК уже создана, пункт меняется в ней же: трек-номер остаётся прежним, штрихкод перевыпускается. Если ещё нет — накладную создадут сразу от нового пункта. Сменить можно, пока заказ не передан в доставку.',
  YANDEX: 'Яндекс не меняет станцию в готовой заявке, поэтому создаётся новая заявка от выбранной станции, а прежняя отменяется. Трек-номер и этикетка будут новыми, покупатель получит уведомление. Если заявки ещё нет — её создадут сразу от новой станции. Сменить можно, пока заказ не передан в доставку.'
}

const HINT: Record<TPickupPointProvider, string> = {
  CDEK: 'Выберите на карте пункт СДЭК, в который продавец сдаст этот заказ.',
  YANDEX: 'Выберите на карте станцию Яндекса, в которую продавец сдаст этот заказ.'
}

export const ShipmentPointPicker: React.FC<IShipmentPointPickerProps> = ({
  order,
  currentAddress,
  onClose,
  onChanged
}) => {
  const provider = getShipmentProvider(order)
  const hasDelivery = Boolean(
    order.delivery_info?.external_id || order.delivery_info?.cdek_uuid
  )
  // У заказов до 10.2026 пункт в заказе не записан — они оформлены от пункта из профиля продавца
  const currentCode =
    order.delivery_info?.shipment_point?.provider === provider
      ? order.delivery_info.shipment_point.code
      : provider === 'YANDEX'
        ? order.seller_id?.yandex_dropoff_station_id ?? null
        : order.seller_id?.nearest_point_of_cdek?.code ?? null
  const mapContainerId = `shipment-point-map-${order._id}`

  const panelRef = React.useRef<HTMLDivElement>(null)
  const mapRef = React.useRef<any>(null)
  const objectManagerRef = React.useRef<any>(null)
  const pointsRef = React.useRef(new Map<string, IMapPoint>())
  const pickedCodeRef = React.useRef<string | null>(null)
  const isCurrentShownRef = React.useRef(false)
  const cdekCellsRef = React.useRef(new Set<string>())
  const quoteRequestRef = React.useRef(0)

  const [mapStart, setMapStart] = React.useState<IMapStart | null>(null)
  const [isMapReady, setIsMapReady] = React.useState(false)
  const [loadingCount, setLoadingCount] = React.useState(0)
  const [hasNoPoints, setHasNoPoints] = React.useState(false)
  const [cities, setCities] = React.useState<IAddressSuggestion[]>([])
  const [city, setCity] = React.useState<ICityOption | null>(null)
  const [picked, setPicked] = React.useState<IMapPoint | null>(null)
  const [quote, setQuote] = React.useState<IShipmentPointQuoteResponse | null>(null)
  const [quoteError, setQuoteError] = React.useState<string | null>(null)
  const [isQuoteLoading, setIsQuoteLoading] = React.useState(false)
  const [isChangeOpen, setIsChangeOpen] = React.useState(false)

  const getPreset = (code: string) =>
    code === pickedCodeRef.current ? PICKED_PRESET : MAP_LOOK[provider].preset

  const repaintPoint = (code: string | null) => {
    if (!code || code === currentCode || !pointsRef.current.has(code)) return
    objectManagerRef.current?.objects.setObjectOptions(code, {
      preset: getPreset(code)
    })
  }

  // Текущий пункт — отдельной меткой вне кластеров: в кластере его не видно.
  // В pointsRef его нет: там пункты загруженных областей, по ним решаем, что догружать
  const showCurrentPoint = (point: IMapPoint) => {
    const map = mapRef.current
    if (!map || isCurrentShownRef.current) return
    isCurrentShownRef.current = true
    const placemark = new window.ymaps.Placemark(
      point.coords,
      {
        hintContent: point.address,
        ...(provider === 'YANDEX' ? { iconCaption: point.name } : {})
      },
      { preset: CURRENT_PRESET, zIndex: 1000 }
    )
    placemark.events.add('click', () => handlersRef.current.pickPoint(point))
    map.geoObjects.add(placemark)
  }

  const addPoints = (list: IMapPoint[]) => {
    const objectManager = objectManagerRef.current
    if (!objectManager) return

    const isFirstBatch = pointsRef.current.size === 0
    const current = list.find((point) => point.code === currentCode)
    if (current) showCurrentPoint(current)
    const fresh = list.filter(
      (point) => point.code !== currentCode && !pointsRef.current.has(point.code)
    )
    fresh.forEach((point) => pointsRef.current.set(point.code, point))
    objectManager.add(
      fresh.map((point) => ({
        id: point.code,
        type: 'Feature',
        geometry: { type: 'Point', coordinates: point.coords },
        properties: {
          hintContent: point.address,
          ...(provider === 'YANDEX' ? { iconCaption: point.name } : {})
        },
        options: { preset: getPreset(point.code) }
      }))
    )
    setHasNoPoints(pointsRef.current.size === 0 && !isCurrentShownRef.current)

    // Координат пункта СДЭК в заказе нет: карта открывается у пункта из профиля продавца,
    // а после загрузки региона переезжает к тому, от которого заказ оформлен сейчас
    if (
      provider === 'CDEK' &&
      isFirstBatch &&
      current &&
      mapStart &&
      distanceKm(current.coords, mapStart.center) > 0.1
    ) {
      mapRef.current?.setCenter(current.coords, POINT_ZOOM)
    }
  }

  const loadPoints = async (request: Promise<IMapPoint[]>) => {
    setLoadingCount((count) => count + 1)
    try {
      addPoints(await request)
    } catch (e) {
      showServerError(e)
    } finally {
      setLoadingCount((count) => count - 1)
    }
  }

  const loadCdekRegion = (coords: TCoords) => {
    cdekCellsRef.current.add(getCdekCell(coords))
    return loadPoints(
      cdekApi
        .getPvzPointsByCoords(coords[0], coords[1])
        .then(({ data }) => data.map(fromCdekOffice))
    )
  }

  const loadVisible = () => {
    const map = mapRef.current
    if (!map) return

    if (provider === 'CDEK') {
      const center = map.getCenter() as TCoords
      if (cdekCellsRef.current.has(getCdekCell(center))) return
      const isLoadedNearby = Array.from(pointsRef.current.values()).some(
        (point) => distanceKm(center, point.coords) < CDEK_LOADED_RADIUS_KM
      )
      if (!isLoadedNearby) loadCdekRegion(center)
      return
    }

    const bounds = map.getBounds() as number[][]
    if (!Array.isArray(bounds) || bounds.length < 2) return
    const latitudes = bounds.map((point) => point[0])
    const longitudes = bounds.map((point) => point[1])
    loadPoints(
      yandexDeliveryApi
        .getDropoffStations({
          available_for_dropoff: true,
          latitude: { from: Math.min(...latitudes), to: Math.max(...latitudes) },
          longitude: { from: Math.min(...longitudes), to: Math.max(...longitudes) }
        })
        .then(({ data }) => data.map(fromYandexStation))
    )
  }

  const pickPoint = async (point: IMapPoint) => {
    const previousCode = pickedCodeRef.current
    pickedCodeRef.current = point.code
    repaintPoint(previousCode)
    repaintPoint(point.code)

    setPicked(point)
    setQuote(null)
    setQuoteError(null)
    const requestId = ++quoteRequestRef.current
    if (point.code === currentCode) {
      setIsQuoteLoading(false)
      return
    }

    try {
      setIsQuoteLoading(true)
      const { data } = await ordersApi.quoteShipmentPoint(order._id, point.code)
      if (requestId === quoteRequestRef.current) setQuote(data)
    } catch (e: any) {
      if (requestId !== quoteRequestRef.current) return
      setQuoteError(
        e?.response?.data?.message ?? 'Не удалось проверить этот пункт'
      )
    } finally {
      if (requestId === quoteRequestRef.current) setIsQuoteLoading(false)
    }
  }

  // Карта подписывается на события один раз, а обработчикам нужны свежие данные заказа
  const handlersRef = React.useRef({ loadVisible, pickPoint })
  React.useEffect(() => {
    handlersRef.current = { loadVisible, pickPoint }
  })

  React.useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [])

  React.useEffect(() => {
    let isCancelled = false

    // Пункт из профиля продавца — текущий, только если заказ оформлен от него же,
    // иначе по нему лишь открываем карту
    const locateCurrentPoint = async (): Promise<{
      coords: TCoords | null
      point: IMapPoint | null
    }> => {
      if (provider === 'CDEK') {
        const profilePoint = order.seller_id?.nearest_point_of_cdek
        if (!profilePoint?.location) return { coords: null, point: null }
        const point = fromCdekOffice(profilePoint)
        return {
          coords: point.coords,
          point: point.code === currentCode ? point : null
        }
      }
      if (!currentCode) return { coords: null, point: null }
      try {
        const { data } = await yandexDeliveryApi.getDropoffStations({
          pickup_point_ids: [currentCode]
        })
        const station = data.find(
          (item) => item.code === currentCode || item.uuid === currentCode
        )
        const point = station ? fromYandexStation(station) : null
        return { coords: point?.coords ?? null, point }
      } catch {
        return { coords: null, point: null }
      }
    }

    locateCurrentPoint().then(({ coords, point }) => {
      if (isCancelled) return
      setMapStart(
        coords && coords.every(Number.isFinite)
          ? { center: coords, zoom: POINT_ZOOM, currentPoint: point }
          : { center: DEFAULT_CENTER, zoom: CITY_ZOOM, currentPoint: null }
      )
    })

    return () => {
      isCancelled = true
    }
  }, [])

  React.useEffect(() => {
    if (!mapStart) return
    let isDestroyed = false
    let debouncedLoadVisible: ReturnType<typeof debounce> | null = null

    const initMap = async () => {
      await loadYandexMaps()
      if (isDestroyed) return
      if (!window.ymaps) {
        return showMessage('error', 'Не удалось загрузить карту', 'Обновите страницу')
      }

      window.ymaps.ready(() => {
        if (isDestroyed || mapRef.current) return

        const map = new window.ymaps.Map(mapContainerId, {
          center: mapStart.center,
          zoom: mapStart.zoom,
          controls: ['geolocationControl', 'zoomControl', 'fullscreenControl']
        })
        const objectManager = new window.ymaps.ObjectManager({
          clusterize: true,
          gridSize: MAP_LOOK[provider].gridSize
        })
        objectManager.clusters.options.set(
          'preset',
          MAP_LOOK[provider].clusterPreset
        )
        objectManager.objects.events.add('click', (event: any) => {
          const point = pointsRef.current.get(String(event.get('objectId') ?? ''))
          if (point) handlersRef.current.pickPoint(point)
        })
        map.geoObjects.add(objectManager)

        mapRef.current = map
        objectManagerRef.current = objectManager
        if (mapStart.currentPoint) showCurrentPoint(mapStart.currentPoint)
        debouncedLoadVisible = debounce(
          () => handlersRef.current.loadVisible(),
          BOUNDS_DELAY
        )
        map.events.add('boundschange', debouncedLoadVisible)
        setIsMapReady(true)
        handlersRef.current.loadVisible()
      })
    }

    initMap()

    return () => {
      isDestroyed = true
      quoteRequestRef.current += 1
      debouncedLoadVisible?.cancel()
      mapRef.current?.destroy()
      mapRef.current = null
      objectManagerRef.current = null
      isCurrentShownRef.current = false
    }
  }, [mapStart])

  React.useEffect(() => {
    if (!city || !isMapReady || !mapRef.current) return

    const coords: TCoords = [city.latitude, city.longitude]
    mapRef.current.setCenter(coords, CITY_ZOOM, { duration: 300 })
    // Город на границе уже загруженного региона иначе остался бы без пунктов
    if (provider === 'CDEK') loadCdekRegion(coords)
  }, [city, isMapReady])

  const getCities = React.useCallback(async (search: string) => {
    if (!search.trim()) {
      setCities([])
      return
    }
    try {
      const { data } = await appApi.getAddresses({
        address: search,
        isCityOnly: 'true'
      })
      setCities(data.suggestions)
    } catch (e) {
      showServerError(e)
    }
  }, [])

  const debouncedGetCities = React.useMemo(
    () => debounce(getCities, DEBOUNCE_DELAY),
    [getCities]
  )

  React.useEffect(() => () => debouncedGetCities.cancel(), [debouncedGetCities])

  const isSuperAdmin = useAppSelector(appSelectors.selectRole) === ERoles.ADMIN
  const currentCost = quote?.current.provider_cost
  const nextCost = quote?.next.provider_cost

  return (
    <div ref={panelRef} className={styles.panel}>
      <div className={styles.header}>
        <Typography.Title level={3} className={styles.title}>
          Пункт отправления
          <Tooltip title={HELP[provider]}>
            <QuestionCircleOutlined className={styles.help} />
          </Tooltip>
        </Typography.Title>
        <Button
          type='text'
          size='middle'
          icon={<CloseOutlined />}
          aria-label='Закрыть'
          onClick={onClose}
        />
      </div>
      <Typography.Text className={styles.line}>
        Сейчас: {currentAddress ?? 'пункт не указан'}
      </Typography.Text>
      <Typography.Text type='secondary' className={styles.line}>
        {HINT[provider]}
      </Typography.Text>

      <Select
        showSearch
        value={city?.name}
        placeholder='Введите город'
        notFoundContent={null}
        filterOption={false}
        size='large'
        className={styles.citySelect}
        onSearch={debouncedGetCities}
        onSelect={(value: string) => setCity(JSON.parse(value) as ICityOption)}
        options={cities.map((item) => ({
          value: JSON.stringify({
            name: item.value,
            latitude: Number(item.data.geo_lat),
            longitude: Number(item.data.geo_lon)
          } satisfies ICityOption),
          label: item.value
        }))}
      />

      <div className={styles.mapWrapper}>
        <div id={mapContainerId} className={styles.map}></div>
        {(!isMapReady || loadingCount > 0) && (
          <div className={styles.loadingOverlay}>
            <Spin />
          </div>
        )}
      </div>

      {hasNoPoints && loadingCount === 0 && (
        <Typography.Text type='secondary' className={styles.emptyText}>
          Пунктов здесь нет. Переместите карту или выберите другой город.
        </Typography.Text>
      )}

      {picked && (
        <div className={styles.picked}>
          <div className={styles.pickedInfo}>
            <Typography.Text strong>{picked.name}</Typography.Text>
            <Typography.Text>{picked.address}</Typography.Text>
            {picked.workTime && (
              <Typography.Text type='secondary'>{picked.workTime}</Typography.Text>
            )}
            {picked.code === currentCode && (
              <Typography.Text type='secondary'>
                Заказ уже оформлен от этого пункта.
              </Typography.Text>
            )}
            {isQuoteLoading && (
              <Typography.Text>
                <Spin size='small' /> Проверяем пункт у перевозчика…
              </Typography.Text>
            )}
            {quoteError && <Alert type='error' showIcon message={quoteError} />}
            {quote && (
              <>
                {isSuperAdmin && (
                  <Typography.Text>
                    Стоимость у перевозчика:{' '}
                    {currentCost !== null && currentCost !== undefined
                      ? `${getNormalizedPrice(currentCost)} → `
                      : ''}
                    <Typography.Text strong>
                      {nextCost !== undefined ? getNormalizedPrice(nextCost) : '—'}
                    </Typography.Text>
                  </Typography.Text>
                )}
                <Typography.Text type='secondary'>
                  Сумма доставки для покупателя не меняется.
                </Typography.Text>
              </>
            )}
          </div>
          <Button
            type='primary'
            size='large'
            disabled={!quote || isQuoteLoading}
            onClick={() => setIsChangeOpen(true)}
          >
            Сменить пункт
          </Button>
        </div>
      )}

      {picked && (
        <ShipmentPointChangeModal
          open={isChangeOpen}
          provider={provider}
          hasDelivery={hasDelivery}
          pointAddress={picked.address}
          isApplied={picked.code === currentCode}
          onConfirm={() => ordersApi.changeShipmentPoint(order._id, picked.code)}
          rereadOrder={onChanged}
          onClose={(isChanged) => {
            setIsChangeOpen(false)
            if (isChanged) onClose()
          }}
        />
      )}
    </div>
  )
}
